import { SNETButton, SNETTextfield, AlertBox, alertTypes } from "shared/dist/components";
import classes from "./DaemonAITab.module.css";
import { useTheme } from "@mui/material";
import { useState, useCallback } from "react";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import clsx from "clsx";
import {
    checkGitRepository,
    initiateDaemonAI,
} from "../../../../../Services/Redux/actionCreators/aiServiceDetailsActions";
import { WarningDialog } from "../../Dialogs/WarningDialog";
import { SuccessDialog } from "../../Dialogs/SuccessDialog";
import { InitiatedDummy } from "../InitiatedDummy";

const GITHUB_ACCOUNT_NAME_REGEXP = /^(?=.{1,39}$)(?!-)(?!.*--)(?!.*-$)[A-Za-z0-9-]+$/;
const GITHUB_REPOSITORY_NAME_REGEXP = /^(?!\.)(?!.*\/)(?!.*\.\.)(?!.*\.git$)[A-Za-z0-9._-]+$/;

export const DaemonAITab = ({ hasService }) => {
    const dispatch = useDispatch();
    const { orgId, serviceId, accountsMatch } = useSelector(
        (state) => ({
            orgId: state.organization.id,
            serviceId: state.aiServiceDetails.id,
            accountsMatch: state.aiServiceDetails.deployments.accountsMatch,
        }),
        shallowEqual
    );

    const [accountName, setAccountName] = useState("");
    const [accountNameValidation, setAccountNameValidation] = useState();
    const [repositoryName, setRepositoryName] = useState("");
    const [repositoryNameValidation, setRepositoryNameValidation] = useState();

    const [repositoryCheckResult, setRepositoryCheckResult] = useState(null);
    const [isAccountMatchWarningOpen, setIsAccountMatchWarningOpen] = useState(false);
    const [isSuccessDialogOpen, setIsSuccessDialogOpen] = useState(false);

    const [error, setError] = useState();

    const getValidationByRegexp = (value, regexp, emptyMessage, invalidMessage) => {
        if (value === "") {
            return { type: alertTypes.ERROR, message: emptyMessage };
        }

        if (!regexp.test(value)) {
            return { type: alertTypes.ERROR, message: invalidMessage };
        }

        return undefined;
    };

    const theme = useTheme();
    const onAccountNameChange = useCallback((event) => {
        const value = event.target.value;
        setError();
        setRepositoryCheckResult(null);
        setAccountName(value);
        setAccountNameValidation(
            getValidationByRegexp(
                value,
                GITHUB_ACCOUNT_NAME_REGEXP,
                "GitHub account name is required.",
                "Use 1-39 characters: letters, numbers or single hyphens. Hyphens can't be at the beginning, end, or repeated."
            )
        );
    }, []);
    const onRepositoryNameChange = useCallback((event) => {
        const value = event.target.value;
        setError();
        setRepositoryCheckResult(null);
        setRepositoryName(value);
        setRepositoryNameValidation(
            getValidationByRegexp(
                value,
                GITHUB_REPOSITORY_NAME_REGEXP,
                "GitHub repository name is required.",
                "Use letters, numbers, dots, underscores or hyphens. Slashes, consecutive dots, leading dots and the .git suffix are not allowed."
            )
        );
    }, []);

    const onCheckRepository = useCallback(async () => {
        try {
            setError();
            setRepositoryCheckResult(null);
            const { data } = await dispatch(checkGitRepository({ accountName, repositoryName }));
            setRepositoryCheckResult(data);
        } catch (error) {
            setError(error?.message || error?.toString());
        }
    }, [dispatch, accountName, repositoryName]);

    const continueInitiateDeployment = useCallback(async () => {
        try {
            setError();
            await dispatch(
                initiateDaemonAI({
                    orgId,
                    serviceId,
                    githubAccountName: accountName,
                    githubRepositoryName: repositoryName,
                })
            );
            setIsSuccessDialogOpen(true);
        } catch (error) {
            setError(error?.message || error?.toString());
        }
    }, [dispatch, orgId, serviceId, accountName, repositoryName]);

    const onInitiateDeployment = useCallback(async () => {
        //INFO: May be undefined, so use the strict equality operator here to avoid an mismatch.
        if (accountsMatch === false) {
            setIsAccountMatchWarningOpen(true);
        } else {
            await continueInitiateDeployment();
        }
    }, [accountsMatch, continueInitiateDeployment]);

    const onAccountMatchWarningOk = useCallback(async () => {
        setIsAccountMatchWarningOpen(false);
        await continueInitiateDeployment();
    }, [continueInitiateDeployment]);

    const isCheckRepositoryValid =
        accountName !== "" &&
        repositoryName !== "" &&
        !accountNameValidation &&
        !repositoryNameValidation;
    const isInitiateValid = isCheckRepositoryValid && repositoryCheckResult?.isInstalled;

    if (hasService) {
        return (
            <div className={clsx(classes.content)}>
                <InitiatedDummy
                    title="Daemon + AI Service already initialized!"
                    description="Your daemon and AI services are already deployed and running."
                />
            </div>
        );
    }
    return (
        <>
            <div className={classes.content}>
                <p className={classes["accent-info"]}>
                    Please see the guides on{" "}
                    <a
                        href="https://dev.singularitynet.io/docs/products/DecentralizedAIPlatform/HaaS/daemon-management/#balance-and-payments"
                        rel="noopener noreferrer"
                        target="_blank"
                    >
                        Service call pricing
                    </a>{" "}
                    and{" "}
                    <a
                        href="https://dev.singularitynet.io/docs/products/DecentralizedAIPlatform/HaaS/preparing-repository"
                        rel="noopener noreferrer"
                        target="_blank"
                    >
                        Repository preparation
                    </a>
                </p>
                <div className={classes["field-wrapper"]}>
                    <SNETTextfield
                        type="text"
                        name="gitHub-account-name"
                        id="gitHub-account-name"
                        className="service-endpoint-input"
                        value={accountName}
                        onChange={onAccountNameChange}
                        onBlur={onAccountNameChange}
                        label="GitHub Account Name"
                        error={accountNameValidation}
                        sx={{
                            "& .MuiInputBase-input": {
                                color: theme.primitiveTokens.palette.main.black,
                                letterSpacing: "0.15px",
                            },
                        }}
                    />
                </div>
                <div className={classes["field-wrapper"]}>
                    <SNETTextfield
                        type="text"
                        name="gitHub-repository-name"
                        id="gitHub-repository-name"
                        className="service-endpoint-input"
                        value={repositoryName}
                        onChange={onRepositoryNameChange}
                        onBlur={onRepositoryNameChange}
                        label="GitHub Repository Name"
                        error={repositoryNameValidation}
                        sx={{
                            "& .MuiInputBase-input": {
                                color: theme.primitiveTokens.palette.main.black,
                                letterSpacing: "0.15px",
                            },
                        }}
                    />
                </div>
                <div className={classes["button-holder"]}>
                    <SNETButton
                        variant="outlined"
                        disabled={!isCheckRepositoryValid}
                        onClick={onCheckRepository}
                    >
                        Check repository
                    </SNETButton>
                </div>
                <div
                    className={clsx(
                        classes["check-result-info"],
                        repositoryCheckResult?.isInstalled ? classes.success : classes.error
                    )}
                >
                    {repositoryCheckResult?.message}
                </div>
                <AlertBox type={alertTypes.ERROR} message={error} />
            </div>
            <SNETButton
                className={classes.button}
                disabled={!isInitiateValid}
                onClick={onInitiateDeployment}
            >
                INITIATE DEPLOYMENT
            </SNETButton>
            <WarningDialog
                isDialogOpen={isAccountMatchWarningOpen}
                onCancel={() => setIsAccountMatchWarningOpen(false)}
                onOk={onAccountMatchWarningOk}
            />
            <SuccessDialog
                isDialogOpen={isSuccessDialogOpen}
                onDialogClose={() => setIsSuccessDialogOpen(false)}
            />
        </>
    );
};
